/**
 * Active Quiz Card Component
 * Shows on the dashboard when there's an active live quiz session
 * Allows users to join/rejoin the quiz
 */

import React, { useEffect, useState, useCallback } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  Animated,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import { router } from "expo-router";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/lib/supabase/client";
import { LiveQuizSession, LiveQuizParticipant } from "../types";

interface ActiveQuizCardProps {
  /** Callback when card is pressed */
  onPress?: () => void;
}

/**
 * Card showing active quiz session that user can join/rejoin
 */
export const ActiveQuizCard: React.FC<ActiveQuizCardProps> = ({ onPress }) => {
  const { activeProfile, userTeam, activeWeekend } = useAuth();
  
  const [activeSession, setActiveSession] = useState<LiveQuizSession | null>(null);
  const [myParticipant, setMyParticipant] = useState<LiveQuizParticipant | null>(null);
  const [quizName, setQuizName] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  
  // Pulse animation for attention
  const pulseAnim = React.useRef(new Animated.Value(1)).current;
  
  /**
   * Check for active quiz sessions
   */
  const checkForActiveQuiz = useCallback(async () => {
    if (!activeProfile?.id) return;
    
    try {
      // Find active live quiz sessions for the current weekend
      const { data: sessions, error } = await supabase
        .from("quiz_live_sessions")
        .select(`
          *,
          quiz:quizzes (id, name, color)
        `)
        .in("status", ["lobby", "playing", "section_break", "showing_ranking"])
        .order("created_at", { ascending: false })
        .limit(1);
      
      if (error) throw error;
      
      if (sessions && sessions.length > 0) {
        const session = sessions[0] as unknown as LiveQuizSession & { quiz: { id: string; name: string; color: string } };
        
        // Check if user's team is participating or can join
        if (userTeam) {
          const { data: participant } = await supabase
            .from("quiz_live_participants")
            .select("*")
            .eq("session_id", session.id)
            .eq("team_id", userTeam.id)
            .maybeSingle();
          
          if (participant) {
            setMyParticipant(participant as unknown as LiveQuizParticipant);
          }
        }
        
        setActiveSession(session);
        setQuizName(session.quiz?.name || "Live Quiz");
      } else {
        setActiveSession(null);
        setMyParticipant(null);
      }
    } catch (err) {
      console.error("Error checking for active quiz:", err);
    } finally {
      setIsLoading(false);
    }
  }, [activeProfile?.id, userTeam, activeWeekend?.id]);
  
  /**
   * Start pulse animation
   */
  useEffect(() => {
    if (activeSession) {
      const pulse = Animated.loop(
        Animated.sequence([
          Animated.timing(pulseAnim, {
            toValue: 1.05,
            duration: 1000,
            useNativeDriver: true,
          }),
          Animated.timing(pulseAnim, {
            toValue: 1,
            duration: 1000,
            useNativeDriver: true,
          }),
        ])
      );
      pulse.start();
      
      return () => pulse.stop();
    }
  }, [activeSession, pulseAnim]);
  
  /**
   * Check on mount and subscribe to changes
   */
  useEffect(() => {
    checkForActiveQuiz();
    
    // Subscribe to session changes
    const channel = supabase
      .channel("active_quiz_check")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "quiz_live_sessions",
        },
        () => {
          checkForActiveQuiz();
        }
      )
      .subscribe();
    
    return () => {
      supabase.removeChannel(channel);
    };
  }, [checkForActiveQuiz]);
  
  /**
   * Handle join/rejoin
   */
  const handlePress = useCallback(() => {
    if (activeSession) {
      if (onPress) {
        onPress();
      } else {
        // Navigate to live quiz screen
        router.push({
          pathname: "/(tabs)/games/quiz/live" as any,
          params: { sessionId: activeSession.id },
        });
      }
    }
  }, [activeSession, onPress]);
  
  // Don't render if loading or no active session
  if (isLoading || !activeSession) {
    return null;
  }
  
  /**
   * Get status message
   */
  const getStatusMessage = () => {
    if (myParticipant) {
      if (myParticipant.status === "disconnected") {
        return "Verbinding verloren - Tik om opnieuw te verbinden";
      }
      if (activeSession.status === "lobby") {
        return myParticipant.is_ready ? "Wachten tot de quiz begint..." : "Tik om klaar te maken";
      }
      return "Quiz is bezig - Tik om terug te gaan";
    }
    
    if (activeSession.status === "lobby") {
      return "Doe mee met de live quiz!";
    }
    
    return "Quiz is al gestart - Spectator modus";
  };
  
  /**
   * Get status color
   */
  const getStatusColor = () => {
    if (myParticipant?.status === "disconnected") {
      return "#EF4444";
    }
    if (activeSession.status === "lobby") {
      return "#10B981";
    }
    return "#F59E0B";
  };
  
  const quizColor = "#8B5CF6";
  
  return (
    <Animated.View
      style={{
        transform: [{ scale: pulseAnim }],
        marginHorizontal: 16,
        marginBottom: 12,
        marginTop: 12,
      }}
    >
      <TouchableOpacity
        onPress={handlePress}
        activeOpacity={0.9}
      >
        <LinearGradient
          colors={[quizColor, "#6366F1"]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          className="rounded-2xl overflow-hidden"
        >
          <View className="p-4">
            {/* Header row with badge and quiz info */}
            <View className="flex-row items-center">
              {/* Live badge */}
              <View className="flex-row items-center mr-3">
                <View 
                  className="w-2 h-2 rounded-full mr-1.5"
                  style={{ backgroundColor: getStatusColor() }}
                />
                <View 
                  className="px-2 py-0.5 rounded-full"
                  style={{ backgroundColor: "rgba(255,255,255,0.2)" }}
                >
                  <Text className="text-white text-xs font-bold uppercase">
                    {activeSession.status === "lobby" ? "Open" : "Live"}
                  </Text>
                </View>
              </View>
              
              {/* Quiz name */}
              <View className="flex-1">
                <Text className="text-white text-lg font-bold" numberOfLines={1}>
                  {quizName}
                </Text>
                <Text className="text-white/80 text-xs" numberOfLines={1}>
                  {getStatusMessage()}
                </Text>
              </View>
              
              {/* Arrow */}
              <View className="items-center justify-center w-10 h-10 rounded-full bg-white/20 ml-2">
                <Ionicons name="arrow-forward" size={20} color="#FFFFFF" />
              </View>
            </View>
            
            {/* Bottom row - question count */}
            <View className="mt-2 flex-row items-center">
              <Ionicons name="help-circle-outline" size={14} color="rgba(255,255,255,0.7)" />
              <Text className="text-white/70 text-xs ml-1">
                {activeSession.total_questions} vragen
              </Text>
              
              {activeSession.status === "playing" && (
                <Text className="text-white/70 text-xs ml-2">
                  • Vraag {(activeSession.current_question_index || 0) + 1}/{activeSession.total_questions}
                </Text>
              )}
              
              {myParticipant?.is_ready && (
                <View className="ml-auto px-2 py-0.5 rounded-full bg-green-500/30">
                  <Text className="text-green-100 text-xs font-medium">
                    ✓ Klaar
                  </Text>
                </View>
              )}
              
              {myParticipant?.status === "disconnected" && (
                <View className="ml-auto flex-row items-center bg-red-500/30 px-2 py-0.5 rounded-full">
                  <Ionicons name="warning" size={12} color="#FEE2E2" />
                  <Text className="text-red-100 text-xs ml-1">
                    Losgekoppeld
                  </Text>
                </View>
              )}
            </View>
          </View>
        </LinearGradient>
      </TouchableOpacity>
    </Animated.View>
  );
};

export default ActiveQuizCard;

